/**
 * PROJ-005: RBAC Security System
 * Permission-based conditional rendering components
 *
 * @module components/auth/CanAccess
 */

import { type ReactNode } from 'react'
import { usePermission, AccessLevel, Entity, Privilege } from '@/hooks/usePermission';
import { useHasAccessLevel } from '@/hooks/useAccessLevel';

/**
 * Props for CanAccess component
 */
export interface CanAccessProps {
  /** The entity to check permission for (e.g., 'users', 'workflows', 'incidents') */
  entity: Entity;

  /** The privilege to check (e.g., 'CREATE', 'READ', 'UPDATE', 'DELETE', 'ARCHIVE') */
  privilege: Privilege;

  /** Content to render if user has permission */
  children: ReactNode;

  /** Optional content to render if user does NOT have permission (default: null) */
  fallback?: ReactNode;

  /**
   * Optional: Require minimum access level in addition to privilege check
   * If specified, user must have BOTH the privilege AND meet the minimum access level
   */
  accessLevel?: AccessLevel;
}

/**
 * Conditionally renders children based on user permissions
 *
 * This component checks if the current user has the specified privilege on the entity.
 * If they do, it renders the children. Otherwise, it renders the fallback (or null).
 *
 * This is the primary way to show/hide UI elements based on RBAC permissions.
 *
 * @component
 * @example Basic usage - Show delete button only if user can delete
 * ```tsx
 * <CanAccess entity="users" privilege="DELETE">
 *   <DeleteButton />
 * </CanAccess>
 * ```
 *
 * @example With fallback - Show disabled button if no permission
 * ```tsx
 * <CanAccess
 *   entity="incidents"
 *   privilege="ARCHIVE"
 *   fallback={<DisabledArchiveButton />}
 * >
 *   <ArchiveButton />
 * </CanAccess>
 * ```
 *
 * @example With access level requirement
 * ```tsx
 * <CanAccess
 *   entity="users"
 *   privilege="UPDATE"
 *   accessLevel={AccessLevel.BUSINESS_UNIT}
 * >
 *   <BulkEditButton />
 * </CanAccess>
 * ```
 *
 * @example Integration with PROJ-017 (Archive/Delete System)
 * ```tsx
 * // In a dialog footer
 * <div className="flex gap-2">
 *   <CanAccess entity="incidents" privilege="DELETE">
 *     <DeleteButton discrete />
 *   </CanAccess>
 *   <CanAccess entity="incidents" privilege="ARCHIVE">
 *     <ArchiveButton />
 *   </CanAccess>
 * </div>
 * ```
 */
export function CanAccess({
  entity,
  privilege,
  children,
  fallback = null,
  accessLevel,
}: CanAccessProps) {
  // Check if user has the privilege
  const hasPrivilege = usePermission(entity, privilege);

  // If access level is specified, also check if user meets the minimum level
  const meetsAccessLevel = accessLevel
    ? useHasAccessLevel(entity, privilege, accessLevel)
    : true;

  // User must have BOTH the privilege AND meet the access level requirement
  const hasPermission = hasPrivilege && meetsAccessLevel;

  if (!hasPermission) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Props for CannotAccess component
 */
export interface CannotAccessProps {
  /** The entity to check permission for */
  entity: Entity;

  /** The privilege to check */
  privilege: Privilege;

  /** Content to render if user does NOT have permission */
  children: ReactNode;

  /**
   * Optional: Check for maximum access level
   * If specified, renders children only if user has LESS than this access level
   */
  accessLevel?: AccessLevel;
}

/**
 * Inverse of CanAccess - renders children only if user does NOT have permission
 *
 * Useful for showing "upgrade your plan" messages, permission request buttons,
 * or other UI elements that should only appear when the user lacks permission.
 *
 * @component
 * @example Show upgrade message for users without delete permission
 * ```tsx
 * <CannotAccess entity="users" privilege="DELETE">
 *   <UpgradePrompt message="Delete permission requires Admin role" />
 * </CannotAccess>
 * ```
 *
 * @example Show request access button
 * ```tsx
 * <CannotAccess entity="security_roles" privilege="CONFIGURE">
 *   <Button variant="outline">
 *     Request Security Admin Access
 *   </Button>
 * </CannotAccess>
 * ```
 */
export function CannotAccess({
  entity,
  privilege,
  children,
  accessLevel,
}: CannotAccessProps) {
  // Check if user has the privilege
  const hasPrivilege = usePermission(entity, privilege);

  // If access level is specified, check if user meets the level
  const meetsAccessLevel = accessLevel
    ? useHasAccessLevel(entity, privilege, accessLevel)
    : true;

  // User must LACK the privilege OR not meet the access level
  const lacksPermission = !hasPrivilege || !meetsAccessLevel;

  if (!lacksPermission) {
    return null;
  }

  return <>{children}</>;
}

/**
 * Export AccessLevel enum for convenience
 * This allows consumers to import everything from this file:
 *
 * @example
 * ```tsx
 * import { CanAccess, CannotAccess, AccessLevel } from '@/components/auth/CanAccess';
 * ```
 */
export { AccessLevel };
